import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import * as PDFJS from 'pdfjs-dist';
import {DocumentViewerPageComponent} from './documentViewerPage.component';
import {WindowRef} from '../../services/windowRef';
import {DocumentType} from '../../models/io/documentType';
import {SETTINGS} from '../../models/settings/settings';
import {CoreSettings} from '../../models/settings/core.settings';

export class DocumentViewerAction {
  constructor(public id: string, public translationKey: string, public icon?: string) {
  }
}

@Component({
    selector: 'lv-document-viewer',
    templateUrl: './documentViewer.component.html',
    styleUrls: ['./documentViewer.component.scss'],
    standalone: false
})
export class DocumentViewerComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input()
  public document: any;

  @Input()
  public type = DocumentType.PDF;
  @ViewChildren(DocumentViewerPageComponent, {read: ElementRef})
  public pageComponents: QueryList<ElementRef>;
  @ViewChild('scollContainer', {read: ElementRef, static: true})
  public scollContainer: ElementRef;
  @Input()
  public actions: DocumentViewerAction[];
  @Input()
  public hideToolbar: boolean;
  @Input()
  public hideSpinner: boolean;
  @Input()
  public hidePager: boolean;
  @Input()
  public hideZoom: boolean;
  @Input()
  public hideRotation: boolean;
  @Input()
  public hideCustomActions: boolean;
  @Input()
  public useFullHeight: boolean;
  @Output()
  public executeAction = new EventEmitter<DocumentViewerAction>();
  @Output()
  public documentRenderedSuccessfully = new EventEmitter<boolean>();

  public documentProxy: any; // PDFDocumentProxy | ..
  public pages: any[];
  public zoomLevel = 100;
  public rotation = 0;
  public currentPage = 0;
  public scrollContainerHeight: number;
  public documentRenderingTerminated: boolean = false;
  private readonly _maxNumberOfRetries: number = 5;
  private _actualNumberOfRetries: number = 0;
  private _componentDestroyed: boolean;

  constructor(private readonly _windowRef: WindowRef,
              @Inject(SETTINGS) private _settings: CoreSettings) {
    switch (this.type) {
      case DocumentType.PDF:
        PDFJS.GlobalWorkerOptions.workerSrc =
            _settings.app_root_url + 'assets/lib/pdfjs/pdf.worker.min.js';
        break;
    }
  }

  @HostListener('window:resize')
  public updateScrollContainerHeight(): void {
    if (!this.useFullHeight) {
      const elementTop = this.scollContainer.nativeElement.getBoundingClientRect().top;
      const windowHeight = this._windowRef.nativeWindow.innerHeight;
      Promise.resolve().then(() => this.scrollContainerHeight = windowHeight - elementTop);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this._renderDocument();
  }

  public scroll(event: Event): void {
    const parent = event.srcElement as HTMLElement;
    const viewPortYStart = parent.offsetTop + parent.scrollTop;
    const viewPortYEnd = viewPortYStart + parent.offsetHeight;
    if (parent) {
      const visibleElements = this.pageComponents.toArray()
      .map((element, index) =>
          // map to keep the original index
          ({nativeElement: element.nativeElement, index})
      )
      // exclude invisible elements
      .filter(element => element.nativeElement.offsetTop + element.nativeElement.offsetHeight > viewPortYStart
          && element.nativeElement.offsetTop < viewPortYEnd)
      .map((element) => {
        // calculate the visible size
        const visibleHeight = Math.min(element.nativeElement.offsetTop + element.nativeElement.offsetHeight, viewPortYEnd)
            - Math.max(element.nativeElement.offsetTop, viewPortYStart);
        const visibleHeightPercent = visibleHeight * 100 / element.nativeElement.offsetHeight;
        return {element, visibleHeightPercent};
      })
      // sort the elements to find the most visible page
      .sort((a, b) => b.visibleHeightPercent - a.visibleHeightPercent);
      if (visibleElements.length) {
        this.currentPage = visibleElements.shift().element.index;
      }
    }
  }

  public scrollTo(pageIndex: number): void {
    this.pageComponents.toArray()[pageIndex].nativeElement.scrollIntoView();
  }

  public ngAfterViewInit(): void {
    this.updateScrollContainerHeight();
  }

  public ngOnDestroy(): void {
    this._componentDestroyed = true;
  }

  private _renderDocument(): void {
    if (!this.document) {
      return;
    }
    switch (this.type) {
      case DocumentType.PDF:
        this._renderPdf();
        break;
    }
  }

  private _renderPdf(): void {
    if (this._componentDestroyed) {
      return;
    }
    PDFJS.getDocument({data: this.document}).promise.then((documentProxy) => {
      this.documentProxy = documentProxy;
      this.pages = Array.from(Array(documentProxy.numPages).keys());
      this.documentRenderedSuccessfully.emit(true);
      this.documentRenderingTerminated = true;
    })
    .catch((err) => {
      this._actualNumberOfRetries++;
      if (this._actualNumberOfRetries >= this._maxNumberOfRetries) {
        // const notification = new Notification('DOKUMENT_SERVICE_UNAVAILABLE', []);
        // notification.duration = 10000;
        if (this._componentDestroyed) {
          return;
        }
        // this._notificationService.show(notification);
        this.documentRenderingTerminated = true;
        return;
      }
      setTimeout(() => {
        this._renderPdf();
      }, 5000);

    });
  }
}
